import React, { useEffect, useState } from 'react';

import Close from '@mui/icons-material/Close';
import FreeBreakfastOutlinedIcon from '@mui/icons-material/FreeBreakfastOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import TaskIcon from '@mui/icons-material/Task';
import {
  Alert,
  Badge,
  Box,
  Divider,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import dayjs from 'dayjs';

import { useEnrolToLessonMutation } from '../../services/classes';
import { Lesson } from '../../services/classes/types';
import { Course } from '../../services/courses/types';
import { ShortStaff, Staff } from '../../services/staff/types';
import { useGetStudentsQuery } from '../../services/students';
import { ShortStudent } from '../../services/students/types';
import { SubjectColorKeys } from '../../theme';
import {
  getLessonClass,
  getLessonCourse,
  getLessonLabel,
  getLessonSubject,
} from '../../utils/common/classes';
import { ENROLLABLE_STUDENT_STATES } from '../../utils/common/constants';
import { getColorKeyFromCourse } from '../../utils/common/courses';
import useError from '../../utils/react/hooks/useError';
import useSuccess from '../../utils/react/hooks/useSuccess';
import Button from '../Button';
import ContentCard from '../ContentCard';
import EditBreakCard from '../EditBreakCard';
import { IconDateTimePicker } from '../IconDatePicker';
import Link from '../Link';
import Modal from '../Modal';
import NotesField from '../NotesField';
import ProfileIcon from '../ProfileIcon';
import 'dayjs/locale/en-au';
import SearchField from '../SearchField';
import StudentCard from '../StudentCard';
import StudentListItem from '../StudentListItem';

import {
  ClassName,
} from './index.styles';

interface LessonDetailsProps {
  lesson: Lesson;
  courses: Course[];
  teacher?: ShortStaff | Staff;
  onClose: () => void;
  classDetailsHref: string;
  toggleRoll?: () => void;
  scheduleMakeup?: () => void;
  isMakeupLoading?: boolean;
  updateLesson?: (attributes: Partial<Lesson>) => void;
  isRollLoading?: boolean;
  renderStudentRow?: (studentId: string) => React.ReactElement;
  disableNoteEdit?: boolean;
  disableTimeEdit?: boolean;
  makeUpScheduled?: boolean;
  editBreak?: (weeks: number) => void;
  isEditBreak?: boolean;
  onEditBreakToggle?: () => void;
  isEditBreakLoading?: boolean;
  onDelete?: () => void;
  onDeleteAllFutureLessons?: () => void;
  onSMS?: () => void;
  toggleHomework?: () => void;
}

const LessonDetails = ({
  lesson,
  courses,
  teacher,
  onClose = () => {},
  classDetailsHref,
  toggleRoll,
  updateLesson = () => {},
  isMakeupLoading,
  scheduleMakeup,
  isRollLoading,
  renderStudentRow,
  disableNoteEdit = false,
  disableTimeEdit = false,
  makeUpScheduled = false,
  editBreak,
  isEditBreak,
  onEditBreakToggle,
  isEditBreakLoading,
  onDelete,
  onDeleteAllFutureLessons,
  onSMS,
  toggleHomework,
}: LessonDetailsProps) => {
  const theme = useTheme();

  const course = getLessonCourse(lesson);
  const className = getLessonClass(lesson);
  const colorKey = getColorKeyFromCourse(course, courses);
  const label = getLessonLabel(lesson, courses);
  const subject = getLessonSubject(lesson);
  const classId = getLessonClass(lesson);

  const [newNotes, setNewNotes] = useState(lesson.notes);

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const [isDateTimePickerTooltipVisible, setIsDateTimePickerTooltipVisible] = useState(false);

  const [searchStr, setSearchStr] = useState('');
  const [enrolFormOpen, setEnrolFormOpen] = useState(false);

  const [selectedStudent, setSelectedStudent] = useState<ShortStudent>();

  const [enrolStudent, {
    isLoading: isEnrolStudentLoading,
    isError: isEnrolStudentError,
    error: enrolStudentError,
    isSuccess: isEnrolStudentSuccess,
  }] = useEnrolToLessonMutation();

  const { data: searchedStudents, isFetching: isSearchedStudentsLoading } = useGetStudentsQuery({
    search: searchStr,
    excludeDisabled: false,
  }, { skip: searchStr === '' || !enrolFormOpen });

  const handleEnrolClose = () => {
    setEnrolFormOpen(false);
    setSelectedStudent(undefined);
  };

  useEffect(() => {
    if (isEnrolStudentSuccess) handleEnrolClose();
  }, [isEnrolStudentSuccess]);

  const handleShowMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleSendSMS = () => {
    handleCloseMenu();
    onSMS?.();
  };

  const handleStudentEnrol = () => {
    if (selectedStudent) {
      enrolStudent({
        subject,
        classId,
        lesson: lesson.lesson,
        studentId: selectedStudent.studentId,
      });
    }
  };

  useEffect(() => {
    setNewNotes(lesson.notes);
  }, [lesson]);

  useError(isEnrolStudentError, enrolStudentError);
  useSuccess(isEnrolStudentSuccess, 'Student enrolled');

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
          >
            <IconButton onClick={onClose}>
              <Close />
            </IconButton>
            <Typography variant="h5" color={theme.palette[colorKey as SubjectColorKeys]?.dark}>
              {label}
            </Typography>
          </Stack>
          {(onSMS || onDelete) && (
            <IconButton onClick={handleShowMenu}>
              <MoreVertIcon />
            </IconButton>

          )}
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleCloseMenu}
          >
            {onSMS && <MenuItem onClick={handleSendSMS}>Copy Student Numbers</MenuItem>}
            <MenuItem
              disabled={lesson.rollMarked}
              onClick={() => {
                setEnrolFormOpen(true);
              }}
            >
              One-off Enrol
            </MenuItem>
            {onDelete && (
              <MenuItem
                onClick={onDelete}
                disabled={lesson.rollMarked}
              >
                Delete Lesson
              </MenuItem>
            )}
            {onDeleteAllFutureLessons && (
              <MenuItem
                onClick={onDeleteAllFutureLessons}
                disabled={lesson.rollMarked}
              >
                Delete All Future Lessons
              </MenuItem>
            )}
          </Menu>
        </Stack>
      </Grid>
      <Grid item xs={12}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1} alignItems="center">
            {/* disabled components do not receive hover events, track the mouse enter and
            leave events to manage native Hover states for the Tooltip  */}
            <div
              onMouseEnter={() => setIsDateTimePickerTooltipVisible(true)}
              onMouseLeave={() => setIsDateTimePickerTooltipVisible(false)}
            >
              <Tooltip title="Class time can only be modified for unmarked lessons" open={isDateTimePickerTooltipVisible && (disableTimeEdit || lesson.rollMarked)} arrow>
                <div>
                  <IconDateTimePicker
                    value={dayjs(lesson.startDatetime)}
                    disabled={disableTimeEdit || lesson.rollMarked}
                    onAccept={(newDate) => {
                      if (newDate) {
                        updateLesson({
                          startDatetime: newDate.valueOf(),
                        });
                      }
                    }}
                  />
                </div>
              </Tooltip>
            </div>

            <Typography variant="h5" color={theme.palette.gray.main}>
              {dayjs(lesson.startDatetime).format('h:mm a, ddd D MMM \'YY')}
            </Typography>
          </Stack>
          {editBreak && (
            <Button
              variant="outlined"
              color="gray"
              size="small"
              onClick={onEditBreakToggle}
              startIcon={<FreeBreakfastOutlinedIcon />}
              disabled={lesson.rollMarked}
            >
              {isEditBreak ? 'Close Edit' : 'Edit Break'}
            </Button>
          )}
        </Stack>
      </Grid>
      {isEditBreak && editBreak && (
        <Grid item xs={12}>
          <EditBreakCard editBreak={editBreak} loading={isEditBreakLoading} />
        </Grid>
      )}
      <Grid item xs={4}>
        <ContentCard>
          <Stack
            direction="column"
            alignItems="center"
            justifyContent="center"
            spacing={1}
          >
            <Typography>
              {teacher?.firstName ?? 'Unassigned'} {teacher?.lastName ? `${teacher?.lastName.charAt(0)}.` : ''}
            </Typography>
            <ProfileIcon
              firstName={teacher?.firstName ?? 'Unassigned'}
              lastName={teacher?.lastName ? `${teacher?.lastName.charAt(0)}.` : ''}
              color={teacher?.profileIcon?.color}
              imageUrl={teacher?.profileIcon?.image}
              size={96}
            />
            <ClassName>
              <Link href={classDetailsHref}>
                {className}
              </Link>
            </ClassName>
          </Stack>
        </ContentCard>
      </Grid>
      <Grid item xs={8} container alignItems="stretch">
        <NotesField
          placeholder="Lesson notes..."
          onChange={(event) => setNewNotes(event.target.value)}
          value={newNotes}
          onBlur={() => updateLesson({
            notes: newNotes,
          })}
          disabled={disableNoteEdit}
        />
      </Grid>
      {renderStudentRow && (
        <Grid item xs={12}>
          <ContentCard variant="outlined">
            <Stack
              direction="row"
              alignItems="center"
              marginBottom={theme.spacing(1)}
              justifyContent="space-between"
            >
              <Typography variant="h5">
                Students
              </Typography>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={theme.spacing(1)}
              >
                {toggleHomework && (
                  <Badge color="error" badgeContent="!" invisible={lesson.homeworkMarked === true}>
                    <Button
                      variant="outlined"
                      color="gray"
                      size="small"
                      onClick={toggleHomework}
                      startIcon={<TaskIcon />}
                    >
                      Homework
                    </Button>
                  </Badge>
                )}
                {toggleRoll && (
                  <Button
                    variant={lesson.rollMarked ? 'outlined' : 'contained'}
                    color="darkPrimary"
                    size="small"
                    onClick={toggleRoll}
                    loading={isRollLoading}
                  >
                    {lesson.rollMarked ? 'Edit Roll' : 'Submit Roll'}
                  </Button>
                )}
                {scheduleMakeup && (
                  <Button
                    variant={makeUpScheduled ? 'outlined' : 'contained'}
                    color="info"
                    size="small"
                    onClick={scheduleMakeup}
                    loading={isMakeupLoading}
                  >
                    {makeUpScheduled ? 'Undo Makeup' : 'Schedule Makeup'}
                  </Button>
                )}
              </Stack>
            </Stack>
            <Divider orientation="horizontal" flexItem />
            <Stack
              direction="column"
              spacing={1}
              divider={<Divider orientation="horizontal" flexItem />}
              marginTop={theme.spacing(1)}
            >
              {lesson.enrolledStudents.length === 0 ? (
                <ContentCard>
                  <Typography variant="overline">No students</Typography>
                </ContentCard>
              ) :
                lesson.enrolledStudents.map((student) => renderStudentRow(student.studentId))}
            </Stack>
          </ContentCard>
        </Grid>
      ) }
      <Modal
        open={enrolFormOpen}
        handleClose={handleEnrolClose}
        title="Enrol Student"
        scrollY
        buttons={
          [
            {
              label: 'Enrol',
              onClick: () => {
                handleStudentEnrol();
              },
              color: 'darkPrimary',
              loading: isEnrolStudentLoading,
              disabled: !selectedStudent,
            },
          ]
        }
      >
        <Stack direction="column" spacing={2}>
          <SearchField
            type="dropdown"
            placeholder="Search students..."
            options={searchedStudents?.data.filter(
              (s) => ENROLLABLE_STUDENT_STATES.includes(s.state)
              && !lesson.enrolledStudents.find(
                (cs) => cs.studentId === s.studentId,
              ),
            )
           || []}
            getOptionKey={(s) => s.studentId}
            getOptionLabel={(s) => s.name}
            onSearch={(search) => setSearchStr(search)}
            loading={isSearchedStudentsLoading}
            onSelect={(s) => setSelectedStudent(s)}
            renderOption={(props, s) => (
              <StudentListItem
                student={s}
                {...props}
                key={s.id}
              />
            )}
            clearOnSelect
          />
          {selectedStudent ? (
            <ContentCard variant="outlined">
              <StudentCard
                student={selectedStudent}
                onClose={() => setSelectedStudent(undefined)}
                showState={false}
              />
            </ContentCard>
          ) : (
            <ContentCard variant="outlined">
              <Box padding={1}>
                <Typography variant="overline">
                  No student selected
                </Typography>
              </Box>
            </ContentCard>
          )}
          <Alert severity="info">
            This student will be enrolled into this lesson only.
            If the student is doing a makeup, use the reschedule button on the roll instead!
          </Alert>
        </Stack>
      </Modal>
    </Grid>
  );
};

export default LessonDetails;
